/**
 * 🌍 Multicultural Holiday Configuration
 *
 * Major holidays across religions and cultures.
 * Uses approximate Gregorian dates for lunar calendars (Islamic, Hebrew, Hindu).
 *
 * Date coverage: 2026-2028 (update annually to keep 2-3 years ahead)
 * Priority system: Higher priority = shown when holidays overlap.
 * Date ranges: [month (0-indexed), startDay, endDay, year (optional)]
 */
export var HOLIDAYS = [
    // ===== CHRISTIAN =====
    {
        id: 'christmas',
        name: 'Christmas',
        emoji: '🎄',
        greeting: 'Merry Christmas! 🎁 Safe travels!',
        dateRanges: [
            { month: 11, startDay: 24, endDay: 26 }, // Dec 24-26 (all years)
        ],
        priority: 10,
        culture: 'christian',
    },
    {
        id: 'christmas-season',
        name: 'Christmas Season',
        emoji: '🎄',
        greeting: 'Happy Holidays! 🎄 Park safe, travel happy!',
        dateRanges: [
            { month: 11, startDay: 20, endDay: 23 }, // Dec 20-23 (all years)
        ],
        priority: 5,
        culture: 'christian',
    },
    {
        id: 'advent',
        name: 'Advent Season',
        emoji: '❄️',
        greeting: 'Season\'s Greetings! ❄️ Secure parking for the holidays',
        dateRanges: [
            { month: 11, startDay: 1, endDay: 19 }, // Dec 1-19 (all years)
        ],
        priority: 3,
        culture: 'christian',
    },
    {
        id: 'easter',
        name: 'Easter',
        emoji: '🐣',
        greeting: 'Happy Easter! 🌷 Safe travels this holiday!',
        dateRanges: [
            // Easter dates vary; approximate ranges for 2026-2028
            { month: 3, startDay: 3, endDay: 6, year: 2026 },
            { month: 2, startDay: 26, endDay: 29, year: 2027 },
            { month: 3, startDay: 14, endDay: 17, year: 2028 }, // Apr 14-17, 2028
        ],
        priority: 8,
        culture: 'christian',
    },
    // ===== ISLAMIC =====
    {
        id: 'ramadan',
        name: 'Ramadan',
        emoji: '🌙',
        greeting: 'Ramadan Kareem! 🌙 Safe travels during this blessed month',
        dateRanges: [
            // Ramadan dates shift ~11 days earlier each year
            { month: 1, startDay: 18, endDay: 28, year: 2026 },
            { month: 1, startDay: 8, endDay: 28, year: 2027 },
            { month: 0, startDay: 28, endDay: 31, year: 2028 }, // Jan 28-31, 2028
        ],
        priority: 9,
        culture: 'islamic',
    },
    {
        id: 'eid-al-fitr',
        name: 'Eid al-Fitr',
        emoji: '🌟',
        greeting: 'Eid Mubarak! 🌟 Wishing you joyful travels!',
        dateRanges: [
            { month: 2, startDay: 20, endDay: 22, year: 2026 },
            { month: 2, startDay: 9, endDay: 11, year: 2027 },
            { month: 1, startDay: 27, endDay: 29, year: 2028 }, // Feb 27-29, 2028
        ],
        priority: 10,
        culture: 'islamic',
    },
    {
        id: 'eid-al-adha',
        name: 'Eid al-Adha',
        emoji: '🕌',
        greeting: 'Eid Mubarak! 🕌 Safe and blessed travels!',
        dateRanges: [
            { month: 4, startDay: 27, endDay: 30, year: 2026 },
            { month: 4, startDay: 16, endDay: 19, year: 2027 },
            { month: 4, startDay: 5, endDay: 8, year: 2028 }, // May 5-8, 2028
        ],
        priority: 10,
        culture: 'islamic',
    },
    // ===== JEWISH =====
    {
        id: 'rosh-hashanah',
        name: 'Rosh Hashanah',
        emoji: '🍎',
        greeting: 'Shanah Tovah! 🍎 Happy New Year and safe travels!',
        dateRanges: [
            { month: 8, startDay: 12, endDay: 14, year: 2026 },
            { month: 9, startDay: 2, endDay: 4, year: 2027 },
            { month: 8, startDay: 21, endDay: 23, year: 2028 }, // Sep 21-23, 2028
        ],
        priority: 9,
        culture: 'jewish',
    },
    {
        id: 'yom-kippur',
        name: 'Yom Kippur',
        emoji: '🕯️',
        greeting: 'G\'mar Chatimah Tovah! 🕯️ Safe travels',
        dateRanges: [
            { month: 8, startDay: 21, endDay: 21, year: 2026 },
            { month: 9, startDay: 11, endDay: 11, year: 2027 },
            { month: 8, startDay: 30, endDay: 30, year: 2028 }, // Sep 30, 2028
        ],
        priority: 9,
        culture: 'jewish',
    },
    {
        id: 'hanukkah',
        name: 'Hanukkah',
        emoji: '🕎',
        greeting: 'Happy Hanukkah! 🕎 Festival of Lights and safe travels!',
        dateRanges: [
            { month: 11, startDay: 4, endDay: 12, year: 2026 },
            { month: 11, startDay: 24, endDay: 31, year: 2027 },
            { month: 11, startDay: 13, endDay: 21, year: 2028 }, // Dec 13-21, 2028
        ],
        priority: 8,
        culture: 'jewish',
    },
    {
        id: 'passover',
        name: 'Passover',
        emoji: '🍷',
        greeting: 'Chag Pesach Sameach! 🍷 Happy Passover!',
        dateRanges: [
            { month: 3, startDay: 2, endDay: 9, year: 2026 },
            { month: 3, startDay: 22, endDay: 29, year: 2027 },
            { month: 3, startDay: 11, endDay: 18, year: 2028 }, // Apr 11-18, 2028
        ],
        priority: 8,
        culture: 'jewish',
    },
    // ===== HINDU =====
    {
        id: 'diwali',
        name: 'Diwali',
        emoji: '🪔',
        greeting: 'Happy Diwali! 🪔 Festival of Lights — safe travels!',
        dateRanges: [
            { month: 10, startDay: 8, endDay: 12, year: 2026 },
            { month: 9, startDay: 29, endDay: 31, year: 2027 },
            { month: 10, startDay: 1, endDay: 2, year: 2027 },
            { month: 9, startDay: 17, endDay: 21, year: 2028 }, // Oct 17-21, 2028
        ],
        priority: 9,
        culture: 'hindu',
    },
    {
        id: 'holi',
        name: 'Holi',
        emoji: '🎨',
        greeting: 'Happy Holi! 🎨 Festival of Colors — safe travels!',
        dateRanges: [
            { month: 2, startDay: 3, endDay: 3, year: 2026 },
            { month: 2, startDay: 22, endDay: 22, year: 2027 },
            { month: 2, startDay: 11, endDay: 11, year: 2028 }, // Mar 11, 2028
        ],
        priority: 8,
        culture: 'hindu',
    },
    // ===== CULTURAL =====
    {
        id: 'lunar-new-year',
        name: 'Lunar New Year',
        emoji: '🧧',
        greeting: 'Happy Lunar New Year! 🧧 Wishing you prosperity and safe travels!',
        dateRanges: [
            { month: 1, startDay: 17, endDay: 17, year: 2026 },
            { month: 1, startDay: 6, endDay: 6, year: 2027 },
            { month: 0, startDay: 26, endDay: 26, year: 2028 }, // Jan 26, 2028
        ],
        priority: 9,
        culture: 'cultural',
    },
    // ===== SECULAR =====
    {
        id: 'new-year',
        name: 'New Year',
        emoji: '🎉',
        greeting: 'Happy New Year! 🎆 Safe travels!',
        dateRanges: [
            { month: 11, startDay: 31, endDay: 31 },
            { month: 0, startDay: 1, endDay: 1 }, // Jan 1 (all years)
        ],
        priority: 10,
        culture: 'secular',
    },
    {
        id: 'thanksgiving',
        name: 'Thanksgiving',
        emoji: '🦃',
        greeting: 'Happy Thanksgiving! 🦃 Safe travels to see loved ones!',
        dateRanges: [
            // 4th Thursday of November (approximate range)
            { month: 10, startDay: 26, endDay: 27, year: 2026 },
            { month: 10, startDay: 25, endDay: 26, year: 2027 },
            { month: 10, startDay: 23, endDay: 24, year: 2028 }, // Nov 23-24, 2028
        ],
        priority: 8,
        culture: 'secular',
    },
];
